import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { QrCode, ArrowLeft, CheckCircle, XCircle, Camera, Warning, Package as PkgIcon } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Flujo:
 * 1) PIN (0256 admin | 0356 uriel)
 * 2) Elegir sucursal (Morelos / América) + solicitar GPS
 * 3) Cámara siempre activa con recuadro tipo etiqueta (rectángulo horizontal)
 *    Uriel apunta a la guía y aprieta "ESCANEAR" -> captura frame -> jsQR decodifica
 * 4) Popup con foto POS + nombre + piezas -> "Marcar como correcto"
 * 5) Lista acumulada + contadores en vivo
 * 6) "Confirmar cierre" -> resumen -> doble confirmación -> Telegram
 */

export default function MLScanner() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const zxingReaderRef = useRef(null);
  const [step, setStep] = useState('pin'); // pin | branch | scan | closed
  const [pin, setPin] = useState('');
  const [auth, setAuth] = useState(null); // {role, name}
  const [branch, setBranch] = useState('morelos');
  const [session, setSession] = useState(null); // {id, folio, branch, ...}
  const [scans, setScans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [popup, setPopup] = useState(null); // {ml_items, photo, shipment_id, pieces}
  const [duplicate, setDuplicate] = useState(null); // {shipment_id, previous_session, previous_scan_at, photo}
  const [finalDlg, setFinalDlg] = useState({ open: false, byProduct: null });
  const [historyDlg, setHistoryDlg] = useState({ open: false, sessions: [], scans: [] });
  const [detected, setDetected] = useState(false); // el QR está siendo detectado ahora mismo
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const autoScanRef = useRef({ lastQr: null, sameCount: 0, cooldownUntil: 0 });

  // ── Cámara ──
  const startCamera = useCallback(async () => {
    if (streamRef.current) return;
    try {
      // Forzamos aspect ratio PORTRAIT: alto > ancho.
      // getUserMedia intenta el stream que más se acerque; los sensores modernos
      // rotan automáticamente para coincidir con la orientación del teléfono.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1080 },
          height: { ideal: 1920 },
          aspectRatio: { ideal: 9 / 16 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // Detectar si el track soporta linterna (torch)
      try {
        const track = stream.getVideoTracks?.()[0];
        const caps = track?.getCapabilities?.();
        setTorchSupported(!!caps?.torch);
      } catch { setTorchSupported(false); }
      // Intentar bloquear en portrait si el navegador lo soporta (no falla si no)
      try {
        if (window.screen?.orientation?.lock) {
          await window.screen.orientation.lock('portrait').catch(() => {});
        }
      } catch { /* no-op */ }
    } catch (e) {
      toast.error(`Cámara: ${e.message}`);
    }
  }, []);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(v => !v);
    } catch (e) {
      toast.error('Este dispositivo no soporta linterna desde el navegador');
      setTorchSupported(false);
    }
  };

  // ── Beep de confirmación (Web Audio API) ──
  const beep = (type = 'ok') => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = type === 'ok' ? 880 : (type === 'error' ? 220 : 500);
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
      setTimeout(() => ctx.close?.(), 300);
    } catch { /* no-op */ }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Inicializar reader ZXing una sola vez (soporta QR + Code128 + Code39 + EAN + UPC)
  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.ITF,
      BarcodeFormat.DATA_MATRIX,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    zxingReaderRef.current = new BrowserMultiFormatReader(hints);
  }, []);

  // Helper: decodifica un canvas con ZXing; devuelve string o null
  const decodeCanvas = (canvas) => {
    try {
      const result = zxingReaderRef.current?.decodeFromCanvas(canvas);
      return result?.getText() || null;
    } catch { return null; }
  };

  useEffect(() => {
    if (step === 'scan') { startCamera(); }
    else { stopCamera(); }
  }, [step, startCamera, stopCamera]);

  // ── PIN ── (después de validar, va al paso de elegir sucursal)
  const submitPin = async () => {
    if (pin.length !== 4) { toast.error('El PIN tiene 4 dígitos'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ml-scanner/auth-pin`, { pin });
      setAuth(data);
      setStep('branch');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PIN incorrecto');
      setPin('');
    } finally { setBusy(false); }
  };

  // ── Iniciar sesión con sucursal manual (sin GPS) ──
  const startSession = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ml-scanner/session/start`, {
        pin, branch,
      });
      setSession(data);
      setStep('scan');
      toast.success(`Folio ${data.folio}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar sesión');
    } finally { setBusy(false); }
  };

  // ── Captura de frame + decodificación (QR o código de barras 1D) ──
  const captureFrame = () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return null;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    // Decodificar con ZXing (soporta QR + Code128 + EAN + más)
    const qr = decodeCanvas(canvas);
    // Comprimir para foto de guía (más chica)
    const smallCanvas = document.createElement('canvas');
    const maxW = 1280;
    const ratio = Math.min(1, maxW / canvas.width);
    smallCanvas.width = Math.round(canvas.width * ratio);
    smallCanvas.height = Math.round(canvas.height * ratio);
    smallCanvas.getContext('2d').drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
    const photoDataUrl = smallCanvas.toDataURL('image/jpeg', 0.72);
    return { qr, photo: photoDataUrl };
  };

  const doScan = async (overrideReason = null, overrideToken = null) => {
    if (busy) return;
    const captured = captureFrame();
    if (!captured) { toast.error('Cámara no lista'); return; }
    if (!captured.qr) { toast.error('No detecté ningún código — reencuadra la guía'); return; }
    setBusy(true);
    // Cooldown para el auto-scanner después de un intento
    autoScanRef.current.cooldownUntil = Date.now() + 3000;
    try {
      const { data } = await axios.post(`${API}/ml-scanner/scan`, {
        session_id: session.id,
        pin,
        qr_text: captured.qr,
        photo_data_url: captured.photo,
        override_reason: overrideReason,
        override_token: overrideToken,
      });
      setScans(prev => [...prev, data.scan]);
      setPopup({
        scan: data.scan,
        photo: captured.photo,
      });
      // Vibración + beep de confirmación
      try { navigator.vibrate?.(60); } catch { /* no-op */ }
      beep('ok');
    } catch (e) {
      const raw = e.response?.data?.detail;
      // Duplicado en sesión (409 con string) o duplicado histórico (409 con objeto)
      if (e.response?.status === 409 && raw && typeof raw === 'object' && raw.code === 'duplicate_history') {
        setDuplicate({ ...raw, photo: captured.photo });
        beep('warn');
      } else {
        const msg = Array.isArray(raw)
          ? raw.map(x => `${(x.loc||[]).slice(-1)}: ${x.msg}`).join(' · ')
          : (typeof raw === 'string' ? raw : (raw?.message || 'Error al escanear'));
        toast.error(msg);
        beep('error');
      }
    } finally {
      setBusy(false);
      autoScanRef.current.lastQr = null;
      autoScanRef.current.sameCount = 0;
    }
  };

  // ── Reanudar cámara cuando se cierra un popup ──
  // iOS Safari pausa el video al abrir un Dialog modal. Al cerrarlo hay que llamar play() otra vez.
  useEffect(() => {
    if (step !== 'scan') return;
    const anyDialogClosed = !popup && !duplicate && !finalDlg.open && !historyDlg.open;
    if (!anyDialogClosed) return;
    const v = videoRef.current;
    if (!v) return;
    // Si el stream se apagó (por permiso revocado, cambio de app, etc.) → reiniciar
    const tracks = streamRef.current?.getTracks?.() || [];
    if (tracks.length === 0 || tracks.every(t => t.readyState === 'ended')) {
      streamRef.current = null;
      startCamera();
      return;
    }
    // Sino, solo reanudar
    v.play().catch(() => {});
  }, [step, popup, duplicate, finalDlg.open, historyDlg.open, startCamera]);

  // ── Loop de detección visual (solo para pintar el recuadro en verde).
  // Ya NO auto-dispara; el escaneo se hace con el botón. Corre en resolución media
  // cada 350ms.
  useEffect(() => {
    if (step !== 'scan') return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (busy || popup || duplicate || finalDlg.open || historyDlg.open) {
        setTimeout(tick, 500);
        return;
      }
      const v = videoRef.current;
      if (!v || v.readyState < 2 || v.videoWidth === 0) {
        setTimeout(tick, 500);
        return;
      }
      try {
        const canvas = document.createElement('canvas');
        // Resolución media (720p) para mejor detección de códigos de barras finos
        const scale = Math.min(1, 720 / v.videoWidth);
        canvas.width = Math.round(v.videoWidth * scale);
        canvas.height = Math.round(v.videoHeight * scale);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        // ZXing decode (QR + Code128 + EAN + más)
        const decoded = decodeCanvas(canvas);
        setDetected(!!decoded);
      } catch { /* silencio */ }
      setTimeout(tick, 350);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, busy, popup, duplicate, finalDlg.open, historyDlg.open]);

  const overridePollRef = useRef({ active: false, token: null });
  const [waitingOverride, setWaitingOverride] = useState(false);

  // ── Solicitar override al admin ──
  const requestOverride = async () => {
    if (!duplicate) return;
    const reason = window.prompt('Motivo del re-escaneo (se enviará al admin):');
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ml-scanner/override-request`, {
        session_id: session.id, pin, shipment_id: duplicate.shipment_id, reason: reason.trim(),
      });
      if (data.telegram_sent_count > 0) {
        toast.success(`Solicitud enviada a ${data.telegram_sent_count} admin(s). Espera la aprobación.`);
      } else {
        toast.error(data.telegram_error || 'No hay admin con Telegram configurado. Contáctalo por otro medio.', { duration: 8000 });
      }
      // Cerrar el modal duplicado y poner en modo "esperando"
      setDuplicate(null);
      overridePollRef.current = { active: true, token: data.token, reason: reason.trim() };
      setWaitingOverride(true);
      const start = Date.now();
      const poll = async () => {
        if (!overridePollRef.current.active) return;
        try {
          const s = await axios.get(`${API}/ml-scanner/override-status`, { params: { token: data.token, pin } });
          if (s.data.approved === true) {
            overridePollRef.current.active = false;
            setWaitingOverride(false);
            toast.success('✅ Aprobado — reintentando escaneo');
            await doScan(reason.trim(), data.token);
            return;
          }
          if (s.data.approved === false) {
            overridePollRef.current.active = false;
            setWaitingOverride(false);
            toast.error('❌ Admin rechazó');
            return;
          }
          if (Date.now() - start < 3 * 60 * 1000) setTimeout(poll, 3000);
          else {
            overridePollRef.current.active = false;
            setWaitingOverride(false);
            toast.error('⏱️ Sin respuesta del admin (3 min). Vuelve a solicitar si es necesario.');
          }
        } catch { /* silencio: reintentar */ }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al solicitar');
    } finally { setBusy(false); }
  };

  const cancelOverrideWait = () => {
    overridePollRef.current.active = false;
    setWaitingOverride(false);
    toast.info('Espera cancelada');
  };

  // ── Diagnóstico Telegram (admin) ──
  const testTelegram = async () => {
    try {
      const { data } = await axios.get(`${API}/ml-scanner/telegram-diagnose`, { params: { pin, test: true } });
      const lines = (data.recipients || []).map(r => `${r.ok ? '✅' : '❌'} ${r.name} (${r.source})${r.error ? ' — ' + r.error : ''}`).join('\n');
      window.alert(
        `Token bot: ${data.telegram_token_configured ? '✅' : '❌'}\n` +
        `Env chat_ids: ${(data.env_chat_ids || []).length}\n` +
        `Total destinatarios: ${(data.recipients || []).length}\n` +
        `Mensajes enviados: ${data.sent}\n\n` +
        (lines || '(sin destinatarios)')
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };
  const revertScan = async (scan) => {
    if (!window.confirm(`¿Deshacer este escaneo?\n\n${scan.shipment_id}\n${scan.pieces} piezas\n\nSe restarán del conteo.`)) return;
    try {
      await axios.delete(`${API}/ml-scanner/scan/${scan.id}`, { params: { pin } });
      setScans(prev => prev.filter(s => s.id !== scan.id));
      toast.success('Escaneo revertido');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo revertir');
    }
  };

  const adminOverrideDirect = async () => {
    if (auth?.role !== 'admin' || !duplicate) return;
    const reason = window.prompt('Motivo del re-escaneo (auditoría):');
    if (!reason || reason.trim().length < 3) return;
    setDuplicate(null);
    await doScan(reason.trim(), null);
  };

  // ── Cierre de sesión ──
  const openCloseDialog = async () => {
    if (scans.length === 0) {
      if (!window.confirm('No hay escaneos. ¿Cerrar de todas formas?')) return;
    }
    // Calcular by_product en local
    const byProd = {};
    for (const s of scans) {
      for (const it of (s.ml_items || [])) {
        const key = it.product_name || it.ml_title || it.ml_item_id || '—';
        if (!byProd[key]) byProd[key] = { packages: 0, pieces: 0 };
        byProd[key].packages += 1;
        byProd[key].pieces += (it.pieces || 0);
      }
    }
    setFinalDlg({ open: true, byProduct: byProd });
  };

  const confirmClose = async () => {
    if (!window.confirm('¿Seguro que quieres cerrar este escaneo? No se podrán agregar más paquetes.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ml-scanner/session/close`, { session_id: session.id, pin });
      toast.success(`✅ Cerrado ${data.folio}`);
      setStep('closed');
      setFinalDlg({ open: false, byProduct: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cerrar');
    } finally { setBusy(false); }
  };

  // ── Historial (admin) ──
  const openHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/ml-scanner/sessions`, { params: { pin, limit: 50 } });
      setHistoryDlg({ open: true, sessions: data, scans: [] });
    } catch (e) { toast.error('No se pudo cargar historial'); }
  };

  const loadHistorySession = async (sess) => {
    try {
      const { data } = await axios.get(`${API}/ml-scanner/session/${sess.id}`, { params: { pin } });
      setHistoryDlg(prev => ({ ...prev, activeSession: data, scans: data.scans || [] }));
    } catch (e) { toast.error('No se pudo cargar'); }
  };

  // ── Totales en vivo ──
  const totalPackages = scans.length;
  const totalPieces = scans.reduce((s, x) => s + (x.pieces || 0), 0);

  // ==================== RENDER ====================
  if (step === 'pin') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center">
              <QrCode size={36} weight="fill" className="text-slate-900" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-1">Escáner Mercado Libre</h1>
          <p className="text-center text-sm text-slate-500 mb-6">Ingresa tu PIN de 4 dígitos</p>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={(e) => e.key === 'Enter' && submitPin()}
            placeholder="••••"
            className="text-center text-3xl tracking-widest h-16 font-mono"
            autoFocus
            data-testid="pin-input"
          />
          <Button
            onClick={submitPin}
            disabled={busy || pin.length !== 4}
            className="w-full mt-4 h-12 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold text-lg"
            data-testid="pin-submit"
          >
            {busy ? 'Validando…' : 'Entrar'}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')} className="w-full mt-2 text-slate-500" data-testid="back-home">
            <ArrowLeft size={16} className="mr-1" /> Volver al POS
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'branch') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
          <p className="text-xs text-slate-500 text-center">Bienvenido</p>
          <p className="text-xl font-bold text-center mb-6">{auth?.name}</p>
          <p className="text-sm font-semibold mb-2">¿Desde qué sucursal?</p>
          <div className="grid grid-cols-2 gap-3">
            {['morelos', 'america'].map(b => (
              <button
                key={b}
                onClick={() => setBranch(b)}
                className={`p-4 rounded-lg border-2 font-bold uppercase transition ${branch === b ? 'border-yellow-400 bg-yellow-50 text-slate-900' : 'border-slate-200 text-slate-500'}`}
                data-testid={`branch-${b}`}
              >
                {b === 'morelos' ? 'Morelos' : 'América'}
              </button>
            ))}
          </div>
          <Button
            onClick={startSession}
            disabled={busy}
            className="w-full mt-6 h-12 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold text-lg"
            data-testid="start-session-btn"
          >
            {busy ? 'Iniciando…' : 'Iniciar conteo'}
          </Button>
          <p className="text-[11px] text-slate-400 mt-3 text-center">
            Se te pedirá permiso de cámara. Los QRs se escanean automáticamente.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'closed') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-8 w-full max-w-sm text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto flex items-center justify-center mb-4">
            <CheckCircle size={48} weight="fill" className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Escaneo cerrado</h1>
          <p className="text-sm text-slate-500 mb-1">Folio</p>
          <p className="font-mono text-lg font-bold mb-4">{session?.folio}</p>
          <p className="text-3xl font-bold text-emerald-600">{totalPackages} paquetes · {totalPieces} piezas</p>
          <p className="text-[11px] text-slate-400 mt-4">Notificación enviada por Telegram ✅</p>
          <Button
            onClick={() => { setStep('pin'); setPin(''); setAuth(null); setSession(null); setScans([]); }}
            className="w-full mt-6"
            data-testid="new-session-btn"
          >
            Iniciar nueva sesión
          </Button>
        </div>
      </div>
    );
  }

  // ── PANTALLA DE SCAN ──
  const branchName = session?.branch === 'morelos' ? 'Morelos' : 'América';
  return (
    <div className="min-h-screen bg-black flex justify-center">
      <div
        className="w-full max-w-md min-h-screen bg-slate-900 text-white flex flex-col relative"
        style={{
          // Extra 40px para que el notch del iPhone NUNCA tape el header
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 40px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
      {/* Header */}
      <div className="bg-slate-800/90 backdrop-blur px-4 py-3 flex items-center justify-between text-xs shrink-0">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-yellow-400 truncate">{session?.folio}</p>
          <p className="text-[10px] text-slate-400">{auth?.name} · {branchName}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {torchSupported && (
            <button
              onClick={toggleTorch}
              className={`px-2 py-1 rounded text-[10px] ${torchOn ? 'bg-yellow-300 text-slate-900 font-bold' : 'bg-slate-700 text-white'}`}
              data-testid="torch-btn"
            >
              🔦 {torchOn ? 'ON' : 'OFF'}
            </button>
          )}
          {auth?.role === 'admin' && (
            <>
              <button onClick={testTelegram} className="px-2 py-1 rounded bg-slate-700 text-[10px]" data-testid="test-telegram-btn" title="Probar notificación Telegram">📱</button>
              <button onClick={openHistory} className="px-2 py-1 rounded bg-slate-700 text-[10px]" data-testid="history-btn">Historial</button>
            </>
          )}
          <button onClick={openCloseDialog} className="px-3 py-1.5 rounded bg-emerald-600 font-bold text-[11px]" data-testid="close-session-btn">
            ✓ CERRAR
          </button>
        </div>
      </div>

      {/* Cámara */}
      <div className="relative flex-1 bg-black overflow-hidden min-h-0">
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="camera-video" />
        {/* Recuadro tipo etiqueta (VERTICAL). VERDE FÓSFORO cuando detecta un QR. */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center px-6">
          <div
            className="relative transition-all duration-150"
            style={{
              width: 'min(65vw, 280px)',
              height: 'calc(min(65vw, 280px) * 1.4)',
              maxHeight: '60vh',
              filter: detected ? 'drop-shadow(0 0 12px #a3e635) drop-shadow(0 0 24px #84cc16)' : 'none',
            }}
          >
            {/* Corners */}
            {['top-0 left-0 border-t-4 border-l-4 rounded-tl',
              'top-0 right-0 border-t-4 border-r-4 rounded-tr',
              'bottom-0 left-0 border-b-4 border-l-4 rounded-bl',
              'bottom-0 right-0 border-b-4 border-r-4 rounded-br'].map((cls, i) => (
              <div key={i} className={`absolute w-10 h-10 ${detected ? 'border-lime-300' : 'border-yellow-400'} ${cls}`} />
            ))}
            <div
              className={`absolute inset-0 rounded-lg ${detected ? 'border-4 border-lime-300' : 'border-2 border-yellow-400/40'}`}
              style={detected ? { boxShadow: '0 0 0 3px rgba(163,230,53,0.5), inset 0 0 30px rgba(163,230,53,0.35)' } : undefined}
            />
            <div className={`absolute -bottom-8 left-0 right-0 text-center text-xs font-black uppercase tracking-wider drop-shadow ${detected ? 'text-lime-300' : 'text-yellow-400'}`}>
              {busy ? 'Escaneando…' : (detected ? '✓ CÓDIGO DETECTADO' : 'Coloca la guía dentro del recuadro')}
            </div>
          </div>
        </div>

        {/* Contador Paquetes */}
        <div className="absolute top-2 left-2 bg-slate-900/80 rounded px-3 py-1.5 text-xs">
          <p className="text-slate-400">Paquetes</p>
          <p className="text-2xl font-bold text-yellow-400" data-testid="total-packages">{totalPackages}</p>
        </div>
      </div>

      {/* Banner "esperando aprobación admin" */}
      {waitingOverride && (
        <div className="bg-orange-500 text-white px-3 py-2 flex items-center justify-between text-xs shrink-0">
          <span>⏳ Esperando aprobación del admin…</span>
          <button onClick={cancelOverrideWait} className="underline" data-testid="cancel-override-wait">Cancelar</button>
        </div>
      )}

      {/* Botón ESCANEAR (verde cuando detecta QR) */}
      <div className="p-4 bg-slate-800 shrink-0">
        <Button
          onClick={() => doScan()}
          disabled={busy}
          className={`w-full h-16 font-black text-2xl rounded-xl shadow-lg text-slate-900 transition-colors ${
            detected
              ? 'bg-lime-300 hover:bg-lime-400 shadow-[0_0_25px_rgba(163,230,53,0.7)]'
              : 'bg-yellow-400 hover:bg-yellow-500'
          }`}
          data-testid="scan-btn"
        >
          <Camera size={32} className="mr-2" weight="bold" />
          {busy ? 'ESCANEANDO…' : (detected ? '✓ ESCANEAR' : 'ESCANEAR')}
        </Button>
      </div>

      {/* Lista scans */}
      {scans.length > 0 && (
        <div className="bg-slate-800 max-h-28 overflow-y-auto border-t border-slate-700 shrink-0">
          <div className="p-2 text-[10px] uppercase tracking-wide text-slate-400 sticky top-0 bg-slate-800">Escaneados en esta sesión</div>
          {[...scans].reverse().map((s, i) => (
            <div key={s.id} className="px-3 py-2 border-t border-slate-700 flex items-center justify-between text-xs">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-slate-400 truncate">#{scans.length - i} · {s.shipment_id}</p>
                <p className="text-white truncate">
                  {(s.ml_items || []).map(it => `${it.product_name || it.ml_title || '?'} × ${it.pieces}`).join(', ') || 'sin ítems'}
                </p>
              </div>
              <p className="text-emerald-400 font-bold ml-2 shrink-0">{s.pieces} pzs</p>
              <button
                onClick={() => revertScan(s)}
                className="ml-2 w-7 h-7 rounded-full bg-red-600/80 hover:bg-red-600 text-white text-xs flex items-center justify-center shrink-0"
                data-testid={`revert-scan-${s.id}`}
                title="Deshacer este escaneo"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Popup confirmación (auto-cierre 2.5s) */}
      <Dialog open={!!popup} onOpenChange={(o) => !o && setPopup(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-emerald-600 flex items-center gap-2">
              <CheckCircle size={22} weight="fill" /> Paquete escaneado
            </DialogTitle>
          </DialogHeader>
          {popup && (
            <div className="space-y-3">
              <p className="font-mono text-[10px] text-slate-400 text-center">{popup.scan?.shipment_id}</p>
              {(popup.scan?.ml_items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  {it.product_image_url ? (
                    <img src={it.product_image_url.startsWith('http') || it.product_image_url.startsWith('data:') ? it.product_image_url : `${process.env.REACT_APP_BACKEND_URL}${it.product_image_url}`} alt="" className="w-16 h-16 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center">
                      <PkgIcon size={28} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-bold text-slate-900">{it.product_name || it.ml_title || '—'}</p>
                    <p className="text-2xl font-black text-emerald-600">{it.pieces} piezas</p>
                    {!it.mapped && (
                      <p className="text-[10px] text-red-500 font-semibold">⚠️ Sin mapeo POS</p>
                    )}
                  </div>
                </div>
              ))}
              {popup.scan?.ml_items?.length === 0 && (
                <p className="text-center text-slate-500 text-sm">Sin ítems detectados en ML</p>
              )}
              <Button
                onClick={() => setPopup(null)}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-bold text-lg"
                data-testid="popup-confirm-ok"
              >
                ✓ MARCAR COMO CORRECTO
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Popup duplicado */}
      <Dialog open={!!duplicate} onOpenChange={(o) => !o && setDuplicate(null)}>
        <DialogContent className="max-w-sm border-red-500 border-2">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <XCircle size={24} weight="fill" /> PAQUETE DUPLICADO
            </DialogTitle>
          </DialogHeader>
          {duplicate && (
            <div className="space-y-3 text-sm">
              <p className="font-mono text-[11px] text-slate-500">{duplicate.shipment_id}</p>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-red-800 font-semibold flex items-center gap-1">
                  <Warning size={16} weight="fill" /> Este paquete ya se escaneó
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Folio anterior: <b>{duplicate.previous_session?.folio || '?'}</b><br />
                  Fecha: {duplicate.previous_scan_at?.slice(0, 16)}
                </p>
              </div>
              {auth?.role === 'admin' ? (
                <>
                  <Button onClick={adminOverrideDirect} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="admin-override-btn">
                    Forzar re-escaneo (admin)
                  </Button>
                  <Button variant="outline" onClick={() => setDuplicate(null)} className="w-full">Cancelar</Button>
                </>
              ) : (
                <>
                  <Button onClick={requestOverride} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="request-override-btn">
                    Solicitar aprobación al admin
                  </Button>
                  <Button variant="outline" onClick={() => setDuplicate(null)} className="w-full">Cancelar</Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo cierre con resumen */}
      <Dialog open={finalDlg.open} onOpenChange={(o) => !o && setFinalDlg({ open: false, byProduct: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resumen del conteo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-blue-50 rounded p-3">
                <p className="text-xs text-slate-500">Paquetes</p>
                <p className="text-3xl font-bold text-blue-700">{totalPackages}</p>
              </div>
              <div className="bg-emerald-50 rounded p-3">
                <p className="text-xs text-slate-500">Piezas totales</p>
                <p className="text-3xl font-bold text-emerald-700">{totalPieces}</p>
              </div>
            </div>
            <div className="border rounded divide-y max-h-64 overflow-y-auto">
              {Object.entries(finalDlg.byProduct || {}).map(([name, v]) => (
                <div key={name} className="p-3 flex justify-between">
                  <span className="font-medium text-slate-800">{name}</span>
                  <span className="text-sm">{v.packages} pkgs · <b>{v.pieces} pzs</b></span>
                </div>
              ))}
              {Object.keys(finalDlg.byProduct || {}).length === 0 && (
                <p className="p-4 text-slate-500 text-center">Sin escaneos aún.</p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setFinalDlg({ open: false, byProduct: null })} className="flex-1">Seguir escaneando</Button>
              <Button onClick={confirmClose} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="final-confirm-close">
                {busy ? 'Cerrando…' : 'Cerrar y notificar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo historial (admin) */}
      <Dialog open={historyDlg.open} onOpenChange={(o) => !o && setHistoryDlg({ open: false, sessions: [], scans: [] })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historial de escaneos</DialogTitle></DialogHeader>
          {!historyDlg.activeSession ? (
            <div className="space-y-1">
              {historyDlg.sessions.map(s => (
                <button key={s.id} onClick={() => loadHistorySession(s)} className="w-full text-left border rounded p-3 hover:bg-slate-50">
                  <div className="flex justify-between text-sm">
                    <span className="font-mono font-bold">{s.folio}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'closed' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-800'}`}>{s.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {s.started_by_name} · {s.branch} · {s.started_at?.slice(0, 16)}
                  </div>
                  <div className="text-xs mt-1">
                    <b>{s.totals?.packages || 0}</b> paquetes · <b>{s.totals?.pieces || 0}</b> piezas
                  </div>
                </button>
              ))}
              {historyDlg.sessions.length === 0 && <p className="text-slate-500 text-center py-6">Sin sesiones aún.</p>}
            </div>
          ) : (
            <div>
              <button onClick={() => setHistoryDlg(prev => ({ ...prev, activeSession: null }))} className="text-sm text-blue-600 mb-2">← Volver</button>
              <p className="font-mono font-bold text-lg">{historyDlg.activeSession.folio}</p>
              <p className="text-xs text-slate-500 mb-3">{historyDlg.activeSession.started_by_name} · {historyDlg.activeSession.branch}</p>
              <div className="space-y-2">
                {historyDlg.scans.map(sc => (
                  <div key={sc.id} className="border rounded p-2 flex gap-2">
                    {sc.photo_url ? (
                      <img src={sc.photo_url.startsWith('http') ? sc.photo_url : `${process.env.REACT_APP_BACKEND_URL}${sc.photo_url}`} alt="" className="w-24 h-16 object-cover rounded cursor-pointer" onClick={() => window.open(sc.photo_url.startsWith('http') ? sc.photo_url : `${process.env.REACT_APP_BACKEND_URL}${sc.photo_url}`, '_blank')} />
                    ) : (
                      <div className="w-24 h-16 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-400 text-center">Sin foto</div>
                    )}
                    <div className="flex-1 text-xs">
                      <p className="font-mono">{sc.shipment_id}</p>
                      <p className="text-slate-600">{(sc.ml_items || []).map(it => `${it.product_name || it.ml_title || '?'} × ${it.pieces}`).join(', ')}</p>
                      <p className="text-slate-400 text-[10px]">{sc.scanned_at?.slice(0, 16)}</p>
                    </div>
                    <p className="text-emerald-600 font-bold self-center">{sc.pieces} pzs</p>
                  </div>
                ))}
                {historyDlg.scans.length === 0 && <p className="text-slate-500 text-center py-4">Sin escaneos.</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
